import type { EmploymentType, WorkerRole, WorkerTier } from '../core/index.js'
/** The messages module's API types: what its endpoints answer and take. */

export type WorkerNotificationType =
  | 'streak_milestone'
  | 'score_update'
  | 'bid_window_approaching'
  | 'coaching'
  | 'tier_within_reach'
  | 'tier_at_risk'
  | 'recovery'
  | 'improvement'
  | 'custom_behavior_prompt'
  | 'period_closing'
  | 'score_final'
  | 'welcome'
  | 'barrier_reply'
  | 'bridge_callout'
  | 'bridge_cancelled'
  | 'bridge_filled'
  | 'bridge_covered'
  | 'bid_result'
  | 'swap_offer'
  | 'open_shift_offer'
  | 'open_shifts_posted'
  | 'schedule_update'
  | 'schedule_reminder'
  | 'bidding_open'
  | 'low_census'
  | 'recall_offer'
  | 'floated'
  | 'staff_message'

export interface WorkerNotification {
  id: string
  type: WorkerNotificationType
  message: string
  createdAt: string
  read: boolean
  /** The fields below are set by the scoring engine; an imported notification has none of them. */
  behaviorKey?: string | null
  pointsAtStake?: number | null
  /** The next step the message suggests. */
  action?: string | null
  dedupeKey?: string
  expiresAt?: string | null
  /** The score snapshot the message was built from. */
  basedOn?: { from: string; to: string; computedAt: string }
  /** On a `staff_message`: which message it is a copy of, the facility and the sender the card names, and whether it went through quiet hours. */
  staffMessage?: { messageId: string; facilityName: string; sentBy: string | null; urgent: boolean }
}

/** Who a roster-wide staff message goes to (SMS plan 3.7). */
export interface StaffMessageFilter {
  role?: WorkerRole
  tier?: WorkerTier
  unitId?: string
  employmentType?: EmploymentType
}

export type StaffMessageRecipients = { workerIds: string[] } | { filter: StaffMessageFilter }

/** Body of `POST /facilities/{facilityId}/messages`. */
export interface SendStaffMessageInput {
  text: string
  to: StaffMessageRecipients
  urgent?: boolean
}

/** A message staff wrote to one worker or several, kept once with its recipients. */
export interface StaffMessage {
  messageId: string
  facilityId: string
  text: string
  urgent: boolean
  to: StaffMessageRecipients
  sentBy: { sub: string; audienceType: string; name: string | null }
  sentAt: string
  recipients: { workerId: string; notificationId: string }[]
  count: number
}

/** How a message reached its recipients, from their receipts. */
export interface StaffMessageReach {
  inApp: number
  texted: number
  held: number
  skipped: number
  failed: number
  noNumber: number
}

export type StaffMessageWithReach = StaffMessage & { reach: StaffMessageReach }

/** Who at the facility marked a worker's barrier seen, and when. */
export interface BarrierAcknowledgement {
  by: { kind: 'operator' | 'facility_administrator'; sub: string }
  at: string
}

/** The facility's answer to a note: who replied, by kind, what they said, and when. One per note. */
export interface BarrierReply {
  by: { kind: 'operator' | 'facility_administrator'; sub: string }
  message: string
  at: string
}

/**
 * A worker's own account of what is getting in the way of one behavior,
 * written from their app once the behavior is costing them points and read
 * by the facility. `standingAt` is the behavior as it stood when they wrote.
 */
export interface WorkerBarrier {
  barrierId: string
  workerId: string
  facilityId: string
  /** The behavior the note is about; null for a general note, whose label is "General". */
  behaviorKey: string | null
  behaviorLabel: string
  comment: string
  /** The behavior as it stood when the worker wrote; null for a general note. */
  standingAt: { value: number; pointsLost: number | null; score: number; period: string | null } | null
  submittedAt: string
  acknowledgement: BarrierAcknowledgement | null
  /** Null until someone at the facility answers; a reply also marks the note seen. */
  reply: BarrierReply | null
}

export interface SubmitBarrierInput {
  /** The behavior the note is about; absent or null for a general note. */
  behaviorKey?: string | null
  comment: string
}

export interface ReplyToBarrierInput {
  message: string
}

/** One thing that happened at a facility (`GET /facilities/{facilityId}/activity`). `kind` says which of the optional fields are set. */
export interface FacilityActivityEntry {
  entryId: string
  kind: 'shift_completed' | 'callout' | 'no_show' | 'behavior' | 'message' | 'bridge_opened' | 'bridge_filled' | 'census_applied' | 'staff_message' | 'schedule_released' | 'schedule_board'
  facilityId: string
  workerId: string
  /** When SeeWhoCares recorded it, not when it happened. */
  at: string
  /** The behaviors the entry bears on; empty for a message. */
  behaviorKeys: string[]
  /** For a completed shift only, judged by the API with the facility's grace minutes. Null otherwise. */
  onTime: boolean | null
  workedToEnd: boolean | null
  /** A completed shift whose event said whether its chart was done; absent when it did not say. */
  chartCompleted?: boolean
  shiftDate?: string | null
  unitId?: string
  scheduledStart?: string
  reportedAt?: string | null
  /** A custom behavior event's own fields. */
  behaviorKey?: string
  occurredAt?: string
  note?: string | null
  /** A message's own fields. */
  messageType?: WorkerNotificationType
  text?: string
  aboutBehaviorKey?: string | null
  /** A Clinical Bridge callout's own fields; for an opening `workerId` is the worker who called out, or empty; for a fill, who took the shift. */
  calloutId?: string
  scheduledEnd?: string
  role?: string
  poolSize?: number
  reason?: string | null
  /** A fill only: from the alert to the acceptance. */
  responseSeconds?: number
  /** A census evaluation applied (census response plan): `workerId` is empty, the workers touched are listed. */
  evaluationId?: string
  census?: number
  verdict?: 'fall' | 'rise'
  flexed?: number
  floated?: number
  recalled?: number
  posted?: number
  workerIds?: string[]
  by?: 'automatic' | 'applied' | 'clock'
  /** A message staff sent (SMS plan 3.7): to one worker (`workerId`) or the roster (`workerId` empty), who sent it and to how many, never the text. */
  messageId?: string
  count?: number
  sentBy?: string | null
  urgent?: boolean
  toKind?: 'workers' | 'roster'
  /** A month released to its workers (month health plan §4): the run, how many were told, what was left unfilled, and what the release went past, if anything. */
  runId?: string
  period?: string
  workers?: number
  shiftsUnfilled?: number
  overrode?: { reasonCode: string; findings: number } | null
  /** The schedule board moved (facility operations dashboard plan D7): a seat posted, put to everyone first come, filled or run short; a swap completed or denied. */
  event?: 'posted' | 'blasted' | 'filled' | 'ran_short' | 'withdrawn' | 'ended_unfilled' | 'swap_completed' | 'swap_denied'
  postingId?: string | null
  swapId?: string | null
  shift?: { date: string; shiftType: string; unitId: string; role: string | null } | null
  source?: string | null
  withWorkerId?: string | null
  mode?: string | null
  reasonCode?: string | null
  /** A board move's own account, in the engine's words from the API (seat life reasons plan D4); absent on an older entry. */
  why?: string | null
}

/** The filter choice that lists the engine's own entries: the census applied, releases, the board's moves, Bridge callouts. */
export const ENGINE_FILTER = 'engine'

/** A behavior the facility could see events for, with how many the window holds. */
export interface FacilityActivityBehavior {
  key: string
  label: string
  direction: 'positive' | 'negative'
  kind: 'standard' | 'custom'
  enabled: boolean
  count: number
}

export interface FacilityActivity {
  since: string
  basis: 'last_sign_in' | 'requested' | 'default_window'
  /** True when the start was pulled forward to the API's 30-day limit. */
  capped: boolean
  previousSignInAt: string | null
  /** First page only. */
  behaviors?: FacilityActivityBehavior[]
  /** First page only. */
  messageCount?: number
  /** The engine's own entries in the window (census applied, releases, the board's moves, Bridge callouts). First page only. */
  engineCount?: number
  /** First page only: every entry in the window, each once. */
  totalCount?: number
  entries: FacilityActivityEntry[]
}

export interface FacilityActivityPage {
  activity: FacilityActivity
  nextCursor: string | null
}

/** What a reading of a facility's activity can ask for: an earlier start, one behavior's events or the messages, and the page to resume from. */
export interface FacilityActivityRequest {
  since: string | null
  /** A behavior key, or `messages`. */
  filter: string | null
  cursor: string | null
}

/** A channel this deployment can send on, as the worker's Notifications card offers it (`available` on `GET /workers/{workerId}/contacts`). */
export interface ChannelDescriptor {
  key: string
  label: string
  verification: 'none' | 'code'
  /** For `web_push`, the VAPID public key the browser subscribes with. */
  publicKey?: string
}

/** One contact's address whole, as `GET /workers/{workerId}/contacts/{contactId}/address` answers it. */
export interface ContactAddress {
  contactId: string
  channel: 'sms' | 'email'
  address: string
}

/** One way a worker has agreed to be reached, as any reader sees it: the address described, never whole. */
export interface ContactView {
  contactId: string
  channel: string
  channelLabel: string
  address: string
  label: string | null
  /** Null for a number staff entered that the worker has not agreed to yet (SMS plan 3.5). */
  consent: { givenAt: string; wording: string; by: 'worker' | 'worker_reply' } | null
  /** Set on a number staff entered, saying when; absent from an API older than 21 October 2026. */
  enteredBy?: { kind: 'staff'; at: string } | null
  /** True while a staff-entered number waits for the worker to agree, in the app or by replying YES. */
  awaitingAgreement?: boolean
  verifiedAt: string | null
  verificationPending: boolean
  lockedUntil: string | null
  enabled: boolean
  /** Set while the worker has opted out of texts by replying STOP; START by reply or the switch clears it. */
  optedOut: { at: string; by: 'reply' | 'provider' } | null
  lastSentAt: string | null
  failures: number
  createdAt: string
}

/** The groups a worker chooses texts by (SMS plan section 3.2). */
export type NotificationGroup = 'shift_alerts' | 'schedule' | 'coaching' | 'from_my_facility'

export interface NotificationGroupDescriptor {
  key: NotificationGroup
  label: string
  description: string
}

export interface ContactsView {
  channels: ContactView[]
  available: ChannelDescriptor[]
  /** Which groups of messages the worker wants by text, every group stated; all on until the worker chooses. Absent from an API older than 21 October 2026. */
  smsGroups?: Record<NotificationGroup, boolean>
  /** The groups in order, with the worker's label and one line for each. */
  groups?: NotificationGroupDescriptor[]
}

/** Body of `PATCH /workers/{workerId}/contacts/preferences`: any of the groups. */
export type UpdateSmsGroupsInput = Partial<Record<NotificationGroup, boolean>>

/** One notification type as the facility's Notifications tab lists it (`GET /facilities/{facilityId}/delivery`). */
export interface DeliveryTypeView {
  type: WorkerNotificationType
  /** Whether a text goes for it at this facility - the facility's own say, else the deployment's policy. */
  texted: boolean
  /** The deployment's policy for it. */
  default: boolean
  /** True for a type that goes through quiet hours. */
  urgent: boolean
}

export interface DeliveryGroupView extends NotificationGroupDescriptor {
  types: DeliveryTypeView[]
}

/** The facility's rules over texts with every type stated. */
export interface FacilityDeliveryView {
  sms: {
    /** Whether this deployment has a text channel at all. */
    offered: boolean
    enabled: boolean
    quietHours: { start: string; end: string } | null
    timezone: string
    groups: DeliveryGroupView[]
  }
}

/** Body of `POST /workers/{workerId}/contacts`. */
export interface RegisterContactInput {
  channel: string
  address: unknown
  consent: { wording: string }
  label?: string
}

export type RegisteredContact = ContactView & { verificationRequired: boolean }

/** One attempt to deliver one notification on one channel (`GET /workers/{workerId}/deliveries`). */
export interface DeliveryReceipt {
  receiptId: string
  workerId: string
  notificationId: string
  notificationType: WorkerNotificationType
  channel: string
  contactId: string | null
  status: 'sent' | 'failed' | 'skipped' | 'address_gone' | 'opted_out' | 'held'
  reason: string | null
  providerMessageId: string | null
  at: string
  /** Billable parts a text went as; null for push and email, absent on older receipts. */
  segments?: number | null
  /** The carrier's final word on a sent text; null until it comes, absent on older receipts. */
  providerOutcome?: { status: 'delivered' | 'failed'; description: string; at: string } | null
  /** On a `held` receipt, when the facility's quiet hours end and the text goes. */
  heldUntil?: string | null
}

export interface ChannelTotals {
  sent: number
  failed: number
  addressGone: number
  /** The billable parts the sends went as (texts; SMS plan D9). Absent from an API older than 21 October 2026. */
  segments?: number
}

/** One client's texts in the overview (SMS plan 3.8). */
export interface ClientDeliveryTotals {
  clientId: string
  organizationName: string
  today: Record<string, ChannelTotals>
  lastSevenDays: Record<string, ChannelTotals>
}

/** `GET /admin/delivery`: what this deployment sends on and how it has gone. */
export interface DeliveryOverview {
  channels: ChannelDescriptor[]
  today: Record<string, ChannelTotals>
  lastSevenDays: Record<string, ChannelTotals>
  days: { day: string; byChannel: Record<string, ChannelTotals> }[]
  /** Every client with anything delivered in the week. Absent from an older API. */
  byClient?: ClientDeliveryTotals[]
}

/** `GET /admin/clients/{clientId}/delivery`: one client's days over a range, with the totals - what texts are metered by. */
export interface ClientDelivery {
  from: string
  to: string
  days: { day: string; byChannel: Record<string, ChannelTotals> }[]
  totals: Record<string, ChannelTotals>
}

/** A worker's reply by text that was not a keyword: their own words to the facility (`GET /workers/{workerId}/inbound-texts`). */
export interface InboundText {
  inboundId: string
  workerId: string
  contactId: string
  body: string
  providerMessageId: string | null
  at: string
}
